import { TimesheetReport } from "domain/entities/timesheet-report";
import { BaseInputFilter } from "domain/models/base-input-filter";
import { useTimesheetReportsAllEmployee } from "presentation/hooks/use-query-timesheet-report";
import { TabPanel, TabView } from "primereact/tabview";
import { Button } from "primereact/button";
import { useEffect, useState, useCallback } from "react";
import "./index.css";
import { useParams } from "react-router-dom";
import { EmployeeInputFilter } from "domain/models/employee-input-filter";
import { useEmployeesByFilter } from "presentation/hooks/use-query-employees";
import { Employee } from "domain/entities/employee";
import {
  exportTimesheetAnalysisToExcel,
  TimesheetAnalysisConfig,
  exportTimesheetAnalysisAsZip,
} from "presentation/utils/exportUtils";
import Spinner from "presentation/components/Shared/Spinner";
import TimesheetLegend from "presentation/components/Shared/TimesheetLegend";
import { TimesheetAnalysisRow } from "presentation/utils/exportUtils/timesheet-analysis-mapper";
import { buildTimesheetAnalysisRow } from "presentation/utils/exportUtils/timesheet-analysis-mapper";
import { buildTimesheetAnalysisData } from "presentation/utils/exportUtils/timesheet-analysis-data-mapper";

const TimesheetAnalysis: React.FC = () => {
  const { year, month } = useParams();

  const parsedYear = !year ? new Date().getFullYear() : parseInt(year);

  const parsedMonth = !month ? new Date().getMonth() + 1 : parseInt(month);

  const { data: timesheetData } = useTimesheetReportsAllEmployee(
    parsedMonth.toString(),
    parsedYear.toString(),
    {
      limit: 500,
    } as BaseInputFilter,
  );

  const { data: employeesData, isLoading } = useEmployeesByFilter({
    limit: 500,
  } as EmployeeInputFilter);

  const [content, setContent] = useState<TimesheetAnalysisRow[]>([]);
  const [employeesWithoutTimesheet, setEmployeesWithoutTimesheet] = useState<
    Employee[]
  >([]);

  const daysMonth = new Date(parsedYear, parsedMonth, 0).getDate();

  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    const processData = async () => {
      if (timesheetData?.data && employeesData?.data) {
        setIsProcessing(true);

        const rows = timesheetData.data.map((report) => {
          const employee = employeesData?.data?.find(
            (emp) =>
              emp.emailAddress.toLowerCase() === report.email.toLowerCase(),
          );

          const analysisData = buildTimesheetAnalysisData(
            report,
            employee,
            daysMonth,
          );

          return buildTimesheetAnalysisRow(
            analysisData,
            parsedYear,
            parsedMonth,
          );
        });

        setContent(rows);

        setIsProcessing(false);
      }

      const activeEmployees = employeesData?.data?.filter(
        (employee) => employee.isActive === true,
      );

      const employeesWithoutTimesheet = activeEmployees
        ?.map((employee) => {
          const ts = timesheetData?.data?.find(
            (t) =>
              t.email.toLowerCase() === employee.emailAddress.toLowerCase() &&
              t.status !== "Novo",
          );

          if (!ts) {
            return employee;
          }

          const allActivities = ts.days.flatMap((day) => day.activities);
          const onlyHasAbsences = allActivities.every(
            (activity) => activity.type.toLowerCase() === "abscence",
          );

          return onlyHasAbsences ? employee : null;
        })
        .filter((item) => item !== null) as Employee[];

      setEmployeesWithoutTimesheet(employeesWithoutTimesheet ?? []);
    };

    processData();
  }, [
    timesheetData?.data,
    employeesData?.data,
    parsedYear,
    parsedMonth,
    daysMonth,
  ]);

  const handleExportExcel = () => {
    const config: TimesheetAnalysisConfig = {
      filename: `analise_timesheet_${parsedMonth}_${parsedYear}`,
      title: `Análise de Timesheet - ${parsedMonth}/${parsedYear}`,
      subtitle: `Gerado em ${new Date().toLocaleString("pt-PT")}`,
      year: parsedYear,
      month: parsedMonth,
      showPageNumbers: true,
    };

    exportTimesheetAnalysisToExcel(content, config);
  };

  const handleExportZip = async () => {
    const config: TimesheetAnalysisConfig = {
      filename: `analise_timesheet_${parsedMonth}_${parsedYear}`,
      title: `Análise de Timesheet - ${parsedMonth}/${parsedYear}`,
      subtitle: `Gerado em ${new Date().toLocaleString("pt-PT")}`,
      year: parsedYear,
      month: parsedMonth,
      showPageNumbers: true,
    };

    await exportTimesheetAnalysisAsZip(config, content);
  };

  return (
    <div>
      <div className="mb-3 flex gap-2 justify-content-end">
        <Button
          label="Exportar Excel"
          icon="pi pi-file-excel"
          className="p-button-success"
          onClick={handleExportExcel}
          disabled={content.length === 0}
        />
        <Button
          label="Exportar PDF"
          icon="pi pi-file-pdf"
          className="p-button-warning"
          onClick={handleExportZip}
          disabled={content.length === 0}
        />
      </div>
      <TabView>
        <TabPanel header="Diário" leftIcon="pi pi-calendar mr-2">
          <TimesheetLegend />
          <table width={"100%"} className="summary-table">
            <thead>
              <tr className="line-border">
                <th align="left">Colaborador</th>
                {Array.from({ length: daysMonth }, (_, index) => (
                  <th key={index + 1}>{index + 1}</th>
                ))}
                <th>H.T.</th>
                <th>Férias</th>
                <th>Ausências</th>
                <th>H.M.</th>
                <th>Total</th>
                <th>Horas Diárias</th>
              </tr>
            </thead>
            <tbody>
              {isLoading || isProcessing ? (
                <tr>
                  <td colSpan={daysMonth + 6} align="center">
                    <Spinner />
                  </td>
                </tr>
              ) : (
                content.map((item, idx) => {
                  return (
                    <tr className="line-border" key={idx}>
                      <td width={"12%"} align="left" title={item.name}>
                        {item.formattedName}
                      </td>
                      {Array.from(item.dailyValues.entries()).map(
                        ([day, cell]) => (
                          <td
                            key={day}
                            width={"2%"}
                            style={{
                              backgroundColor: cell.color,
                              padding: 0,
                              textAlign: "center",
                            }}
                          >
                            {cell.value}
                          </td>
                        ),
                      )}

                      <td width={"4%"}>{item.totalHours}</td>
                      <td width={"4%"}>{item.vacationHours}</td>
                      <td width={"4%"}>{item.absenceHours}</td>
                      <td width={"4%"}>{item.availableHours}</td>
                      <td width={"4%"}>{item.totalHoursAll}</td>
                      <td width={"4%"}>{item.workHoursPerDay}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </TabPanel>
        <TabPanel header="Resumo" leftIcon="pi pi-calculator mr-2">
          <TimesheetLegend />
          <table width={"100%"} className="summary-table">
            <thead>
              <tr className="line-border">
                <th align="left">Colaborador</th>
                <th>D.T.</th>
                <th>Férias</th>
                <th>D.A.</th>
                <th>D.M.</th>
                <th>Total</th>
                <th>P.T.</th>
                <th>P.A.</th>
                <th>Prevenção</th>
                <th>Deslocação</th>
                <th>Horas Extra</th>
                <th>Horas Diárias</th>
              </tr>
            </thead>
            <tbody>
              {isLoading || isProcessing ? (
                <tr>
                  <td colSpan={daysMonth + 6} align="center">
                    <Spinner />
                  </td>
                </tr>
              ) : (
                content.map((item, idx) => {
                  return (
                    <tr className="line-border" key={idx}>
                      <td width={"12%"} align="left" title={item.name}>
                        {item.name}
                      </td>
                      <td width={"4%"}>{item.daysWorked}</td>
                      <td width={"4%"}>{item.vacationDays}</td>
                      <td width={"4%"}>{item.absenceDays}</td>
                      <td width={"4%"}>{item.availableDays}</td>
                      <td width={"4%"}>{item.totalDaysAll}</td>
                      <td width={"4%"}>{item.percentWorked}%</td>
                      <td width={"4%"}>{item.percentAbsent}%</td>
                      <td width={"4%"}>{item.preventionsCount}</td>
                      <td width={"4%"}>{item.displacementCount}</td>
                      <td width={"4%"}>{item.extraHourCount}</td>
                      <td width={"4%"}>{item.workHoursPerDay}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </TabPanel>
        <TabPanel header="Pendentes" leftIcon="pi pi-exclamation-triangle mr-2">
          <table width={"70%"} className="summary-table">
            <thead>
              <tr className="line-border">
                <th>Funcionário</th>
                <th>E-mail</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {employeesWithoutTimesheet.map((employee) => (
                <tr key={employee.id} className="line-border">
                  <td align="left">{employee.name}</td>

                  <td align="left">{employee.emailAddress}</td>
                  <td align="left">
                    {employee.isActive ? "Ativo" : "Inativo"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TabPanel>
      </TabView>
    </div>
  );
};

export default TimesheetAnalysis;
